"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RELATIVE_DESC = exports.RELATIVE_ASC = exports.DATABASE_PROJECTION_UNGROUPED = void 0;
exports.databaseProjectionOptions = databaseProjectionOptions;
exports.normalizeDatabaseProjectionCell = normalizeDatabaseProjectionCell;
exports.databaseProjectionDisplayValue = databaseProjectionDisplayValue;
exports.databaseProjectionDefaultGroups = databaseProjectionDefaultGroups;
exports.databaseProjectionColumnHoldsDays = databaseProjectionColumnHoldsDays;
exports.databaseProjectionGroupValues = databaseProjectionGroupValues;
exports.sortDatabaseProjectionRelativeKeys = sortDatabaseProjectionRelativeKeys;
exports.sortDatabaseProjectionNumericKeys = sortDatabaseProjectionNumericKeys;
exports.databaseProjectionDateKeyComparator = databaseProjectionDateKeyComparator;
exports.compareDatabaseProjectionDateGroupKeys = compareDatabaseProjectionDateGroupKeys;
const hash_1 = __importDefault(require("@emotion/hash"));
const date_fns_1 = require("date-fns");
exports.DATABASE_PROJECTION_UNGROUPED = { key: 'Ungroups', value: null };
function databaseProjectionOptions(column) {
    const options = column.data.options ??
        (column.logicalType?.name === 'Tag' ? column.logicalType.data : undefined);
    return Array.isArray(options)
        ? options.filter((option) => !!option &&
            typeof option.id === 'string' &&
            typeof option.value === 'string')
        : [];
}
/** JSON property values, without renderer objects or live Yjs bindings. */
function normalizeDatabaseProjectionCell(raw, column) {
    const value = raw && typeof raw === 'object' && 'columnId' in raw && 'value' in raw
        ? raw.value
        : raw;
    if (column.type === 'number' ||
        column.type === 'progress' ||
        column.type === 'date' ||
        column.type === 'created-time' ||
        column.type === 'updated-time') {
        return typeof value === 'number' ? value : null;
    }
    if (column.type === 'multi-select')
        return Array.isArray(value) ? value : [];
    if (column.type === 'select')
        return typeof value === 'string' ? value : null;
    if (column.type === 'checkbox')
        return typeof value === 'boolean' ? value : false;
    if (column.type === 'text' ||
        column.type === 'title' ||
        column.type === 'rich-text') {
        if (typeof value === 'string')
            return value;
        // Raw Yjs text is serialized as its delta, never retained as a renderer.
        const delta = Array.isArray(value)
            ? value
            : value && typeof value === 'object' && 'delta' in value
                ? value.delta
                : undefined;
        if (Array.isArray(delta))
            return delta
                .map(part => (typeof part?.insert === 'string' ? part.insert : ''))
                .join('');
        return value ?? '';
    }
    return value;
}
/** Search uses the same select labels displayed by the property codec. */
function databaseProjectionDisplayValue(value, column) {
    if (column.type !== 'select' && column.type !== 'multi-select')
        return value;
    const options = databaseProjectionOptions(column);
    const label = (id) => options.find(option => option.id === id)?.value ?? '';
    return Array.isArray(value) ? value.map(label).join(',') : label(value);
}
/** Extracted defaultKeys/valuesGroup kernel; frontend keeps its renderer config. */
function databaseProjectionDefaultGroups(column, name) {
    if (name === 'select' || name === 'multi-select') {
        return [
            ...databaseProjectionOptions(column).map(option => ({
                key: option.id,
                value: option.id,
            })),
            exports.DATABASE_PROJECTION_UNGROUPED,
        ];
    }
    if (name === 'boolean')
        return [
            { key: 'true', value: true },
            { key: 'false', value: false },
            exports.DATABASE_PROJECTION_UNGROUPED,
        ];
    return [exports.DATABASE_PROJECTION_UNGROUPED];
}
/**
 * Whether the values of a column are days: a date column without a time keeps
 * UTC midnight of each calendar day; every other date value is a moment.
 */
function databaseProjectionColumnHoldsDays(column) {
    return column.type === 'date' && column.data.includeTime !== true;
}
function databaseProjectionGroupValues(column, groupBy, value, nowMs) {
    if (groupBy.name === 'select')
        return value == null
            ? [exports.DATABASE_PROJECTION_UNGROUPED]
            : [{ key: `${value}`, value }];
    if (groupBy.name === 'multi-select')
        return Array.isArray(value) && value.length
            ? value.map(id => ({ key: `${id}`, value: id }))
            : [exports.DATABASE_PROJECTION_UNGROUPED];
    if (groupBy.name === 'text')
        return typeof value === 'string' && value
            ? [{ key: (0, hash_1.default)(value), value }]
            : [exports.DATABASE_PROJECTION_UNGROUPED];
    if (groupBy.name === 'number')
        return typeof value === 'number'
            ? [{ key: `g:${Math.floor(value / 10)}`, value: Math.floor(value / 10) }]
            : [exports.DATABASE_PROJECTION_UNGROUPED];
    if (groupBy.name === 'boolean')
        return typeof value === 'boolean'
            ? [{ key: String(value), value }]
            : [exports.DATABASE_PROJECTION_UNGROUPED];
    if (!groupBy.name.startsWith('date-'))
        throw new Error(`Unsupported database group configuration: ${groupBy.name}`);
    if (value == null)
        return [exports.DATABASE_PROJECTION_UNGROUPED];
    const days = databaseProjectionColumnHoldsDays(column);
    // A day is grouped on its calendar day, a moment on its local day; a group
    // of days keeps UTC midnight, as the column it is written into.
    const stored = new Date(value);
    const date = days
        ? new Date(stored.getUTCFullYear(), stored.getUTCMonth(), stored.getUTCDate())
        : (0, date_fns_1.startOfDay)(stored);
    const groupValue = (group) => days
        ? Date.UTC(group.getFullYear(), group.getMonth(), group.getDate())
        : +group;
    let group = date;
    if (groupBy.name === 'date-relative') {
        const today = (0, date_fns_1.startOfDay)(new Date(nowMs));
        const offset = (0, date_fns_1.differenceInCalendarDays)(date, today);
        const key = offset === 0
            ? 'today'
            : offset === 1
                ? 'tomorrow'
                : offset === -1
                    ? 'yesterday'
                    : offset > 0 && offset <= 7
                        ? 'next7'
                        : offset > 0 && offset <= 30
                            ? 'next30'
                            : offset < 0 && -offset <= 7
                                ? 'last7'
                                : offset < 0 && -offset <= 30
                                    ? 'last30'
                                    : undefined;
        if (key)
            return [{ key, value: groupValue(date) }];
        group = (0, date_fns_1.startOfMonth)(date);
    }
    else if (groupBy.name === 'date-week-sun')
        group = (0, date_fns_1.startOfWeek)(date, { weekStartsOn: 0 });
    else if (groupBy.name === 'date-week-mon')
        group = (0, date_fns_1.startOfWeek)(date, { weekStartsOn: 1 });
    else if (groupBy.name === 'date-month')
        group = (0, date_fns_1.startOfMonth)(date);
    else if (groupBy.name === 'date-year')
        group = (0, date_fns_1.startOfYear)(date);
    else if (groupBy.name !== 'date-day')
        throw new Error(`Unsupported database group configuration: ${groupBy.name}`);
    return [{ key: String(groupValue(group)), value: groupValue(group) }];
}
exports.RELATIVE_ASC = [
    'last30',
    'last7',
    'yesterday',
    'today',
    'tomorrow',
    'next7',
    'next30',
];
exports.RELATIVE_DESC = [...exports.RELATIVE_ASC].reverse();
/**
 * Sorts relative date keys in chronological order
 */
function sortDatabaseProjectionRelativeKeys(a, b, asc, nowMs = Date.now()) {
    const order = asc ? exports.RELATIVE_ASC : exports.RELATIVE_DESC;
    const idxA = order.indexOf(a);
    const idxB = order.indexOf(b);
    if (idxA !== -1 && idxB !== -1)
        return idxA - idxB;
    const numericA = Number(a);
    const numericB = Number(b);
    const aIsNumeric = Number.isFinite(numericA);
    const bIsNumeric = Number.isFinite(numericB);
    if (aIsNumeric && bIsNumeric) {
        return asc ? numericA - numericB : numericB - numericA;
    }
    const today = new Date(nowMs);
    today.setHours(0, 0, 0, 0);
    const compareNumericToRelative = (numeric) => numeric < +today ? -1 : 1;
    if (aIsNumeric && idxB !== -1) {
        const result = compareNumericToRelative(numericA);
        return asc ? result : -result;
    }
    if (idxA !== -1 && bIsNumeric) {
        const result = compareNumericToRelative(numericB);
        return asc ? -result : result;
    }
    return 0; // Both not found
}
/**
 * Sorts numeric date keys (timestamps)
 */
function sortDatabaseProjectionNumericKeys(a, b, asc) {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return asc ? na - nb : nb - na;
    }
    return 0; // Not both numeric
}
function databaseProjectionDateKeyComparator(mode, asc, nowMs = Date.now()) {
    return (a, b) => {
        if (a === 'Ungroups' && b === 'Ungroups')
            return 0;
        if (a === 'Ungroups')
            return 1;
        if (b === 'Ungroups')
            return -1;
        if (mode === 'date-relative') {
            // Try relative key sorting first
            const relativeResult = sortDatabaseProjectionRelativeKeys(a, b, asc, nowMs);
            if (relativeResult !== 0)
                return relativeResult;
            // Try numeric sorting second
            const numericResult = sortDatabaseProjectionNumericKeys(a, b, asc);
            if (numericResult !== 0)
                return numericResult;
            // Fallback to lexicographic order for mixed cases
            return asc ? a.localeCompare(b) : b.localeCompare(a);
        }
        // Standard numeric/lexicographic comparison for other date modes
        return (sortDatabaseProjectionNumericKeys(a, b, asc) ||
            (asc ? a.localeCompare(b) : b.localeCompare(a)));
    };
}
function compareDatabaseProjectionDateGroupKeys(mode, left, right, asc, nowMs) {
    return databaseProjectionDateKeyComparator(mode, asc, nowMs)(left, right);
}
